/**
 * <amazon-buy-button>
 *
 * Geo-targeted "Buy from Amazon" text link rendered by snippets/amazon-buy-button.liquid inside <buy-buttons>.
 *
 * - The element is rendered with the `hidden` attribute and one `data-url-{cc}` attribute per configured Amazon
 *   marketplace (e.g. data-url-us, data-url-de). Optional `data-country-aliases="AT=DE, IE=GB"` maps extra
 *   countries onto a configured marketplace.
 * - The visitor country is detected client-side from the IP through https://get.geojs.io/v1/ip/country.json and
 *   cached in sessionStorage under "theme:geo-country" (same key as sections/geo-discount-bar.liquid).
 * - When a link exists for the visitor country the link is revealed, otherwise it stays hidden.
 *
 * Layout (see .buy-buttons--amazon rules in assets/custom.css):
 *   Two columns (desktop)                      One column (mobile)
 *   [ Add to cart      ] [ Shop Pay        ]    [ Add to cart      ]
 *   Buy from Amazon    More payment options      Buy from Amazon
 *                                               [ Shop Pay         ]
 *                                                More payment options
 *
 * The "More payment options" link is rendered by Shopify inside <shopify-accelerated-checkout> (light DOM, so it
 * can be styled but not moved). In the two column layout the dynamic checkout cell spans two grid rows so the link
 * shares the second row with the Amazon link; its top margin is measured here so both texts line up exactly (the
 * add to cart and Shop Pay buttons have different heights depending on theme settings).
 */
(function () {
  if (window.customElements.get('amazon-buy-button')) {
    return;
  }

  var GEO_ENDPOINT = 'https://get.geojs.io/v1/ip/country.json';
  var GEO_STORAGE_KEY = 'theme:geo-country';
  var COLUMNS_CLASS = 'buy-buttons--amazon-columns';
  var OFFSET_PROPERTY = '--amazon-more-options-offset';

  var countryPromise = null;

  function readCachedCountry() {
    try {
      return sessionStorage.getItem(GEO_STORAGE_KEY);
    } catch (error) {
      return null;
    }
  }

  function cacheCountry(country) {
    try {
      sessionStorage.setItem(GEO_STORAGE_KEY, country);
    } catch (error) {
      // Storage may be unavailable (e.g. Safari private mode); ignore.
    }
  }

  /**
   * Resolves the visitor ISO 3166-1 alpha-2 country code ("" when unknown). Shared by every instance on the page.
   */
  function getVisitorCountry() {
    var cached = readCachedCountry();

    if (cached) {
      return Promise.resolve(cached.toUpperCase());
    }

    if (!countryPromise) {
      countryPromise = fetch(GEO_ENDPOINT)
        .then(function (response) {
          return response.json();
        })
        .then(function (data) {
          var country = ((data && data.country) || '').toUpperCase();

          if (country) {
            cacheCountry(country);
          }

          return country;
        })
        .catch(function () {
          return '';
        });
    }

    return countryPromise;
  }

  class AmazonBuyButton extends HTMLElement {
    connectedCallback() {
      this.link = this.querySelector('a');

      if (!this.link) {
        return;
      }

      var links = this.getLinks();
      var countries = Object.keys(links);

      if (countries.length === 0) {
        return;
      }

      // Inside the theme editor always show the first configured link so merchants can see and style the button.
      if (window.Shopify && window.Shopify.designMode) {
        this.reveal(links[countries[0]]);
        return;
      }

      getVisitorCountry().then(function (country) {
        if (!this.isConnected) {
          return;
        }

        var url = this.resolveUrl(links, country);

        if (url) {
          this.reveal(url);
        }
      }.bind(this));
    }

    disconnectedCallback() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
    }

    /**
     * Collects the configured links from data-url-xx attributes: { US: "https://...", DE: "https://..." }
     */
    getLinks() {
      var links = {};

      Object.keys(this.dataset).forEach(function (key) {
        if (/^url[a-z]{2}$/i.test(key) && this.dataset[key]) {
          links[key.slice(3).toUpperCase()] = this.dataset[key];
        }
      }.bind(this));

      return links;
    }

    resolveUrl(links, country) {
      if (!country) {
        return null;
      }

      country = country.toUpperCase();

      if (links[country]) {
        return links[country];
      }

      var target = null;

      (this.dataset.countryAliases || '').split(',').forEach(function (pair) {
        var parts = pair.split('=');

        if (parts.length === 2 && parts[0].trim().toUpperCase() === country) {
          target = parts[1].trim().toUpperCase();
        }
      });

      return target && links[target] ? links[target] : null;
    }

    reveal(url) {
      this.link.href = url;
      this.link.rel = 'noopener';
      this.hidden = false;

      this.buyButtons = this.closest('buy-buttons');

      if (!this.buyButtons) {
        return;
      }

      this.buyButtons.classList.add('buy-buttons--amazon');
      this.syncLayout();

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.syncLayout.bind(this));
        this.resizeObserver.observe(this.buyButtons);

        var paymentButton = this.buyButtons.querySelector('.shopify-payment-button');

        if (paymentButton) {
          this.resizeObserver.observe(paymentButton);
        }
      }
    }

    /**
     * Detects whether the dynamic checkout button sits beside the Amazon link (two columns) and, if so, aligns the
     * top of the "More payment options" link with the top of the Amazon link so both sit on the same row.
     */
    syncLayout() {
      var buyButtons = this.buyButtons;

      if (!buyButtons || !this.isConnected) {
        return;
      }

      var paymentButton = buyButtons.querySelector('.shopify-payment-button');

      if (!paymentButton || paymentButton.offsetParent === null) {
        this.resetLayout();
        return;
      }

      var amazonRect = this.getBoundingClientRect();
      var paymentRect = paymentButton.getBoundingClientRect();
      var sideBySide = paymentRect.width > 0 && paymentRect.left >= amazonRect.right - 1;

      buyButtons.classList.toggle(COLUMNS_CLASS, sideBySide);

      if (!sideBySide) {
        this.resetLayout();
        return;
      }

      var moreOptions = paymentButton.querySelector('.shopify-payment-button__more-options');

      if (!moreOptions) {
        buyButtons.style.removeProperty(OFFSET_PROPERTY);
        return;
      }

      // Re-measure: toggling the column class may have moved the Amazon link.
      var amazonLinkRect = this.link.getBoundingClientRect();
      var moreOptionsRect = moreOptions.getBoundingClientRect();
      var currentOffset = parseFloat(getComputedStyle(moreOptions).marginTop) || 0;
      var nextOffset = Math.max(0, currentOffset + (amazonLinkRect.top - moreOptionsRect.top));

      if (Math.abs(nextOffset - currentOffset) < 0.5) {
        return;
      }

      buyButtons.style.setProperty(OFFSET_PROPERTY, Math.round(nextOffset * 100) / 100 + 'px');
    }

    resetLayout() {
      if (!this.buyButtons) {
        return;
      }

      this.buyButtons.classList.remove(COLUMNS_CLASS);
      this.buyButtons.style.removeProperty(OFFSET_PROPERTY);
    }
  }

  window.customElements.define('amazon-buy-button', AmazonBuyButton);
})();
